import { inject, Injectable } from "@angular/core";
import { UserWeeklyScheduleService } from "../../../api/services";
import { Observable } from "rxjs";
import {
  CreateUserWeeklyScheduleDto,
  ResponseUserWeeklyScheduleDto,
  WeeklyScheduleDto,
} from "../../../api/models";
import { UserWeeklyScheduleControllerCreate$Params } from "../../../api/fn/user-weekly-schedule/user-weekly-schedule-controller-create";
import { environment } from "../../../environments/environment";
import { LoggedUserService } from "@keus-template/auth";
import { UserWeeklyScheduleControllerUpdate$Params } from "../../../api/fn/user-weekly-schedule/user-weekly-schedule-controller-update";
import { UserWeeklyScheduleControllerDelete$Params } from "../../../api/fn/user-weekly-schedule/user-weekly-schedule-controller-delete";
import { UserWeeklyScheduleControllerFindOne$Params } from "../../../api/fn/user-weekly-schedule/user-weekly-schedule-controller-find-one";

@Injectable({
  providedIn: "root",
})
export class UserWeeklyScheduleApiService {
  /** Injected service for logged user */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** Service for interacting with the User Weekly Schedule API. */
  private readonly _userWeeklyScheduleService: UserWeeklyScheduleService =
    inject(UserWeeklyScheduleService);

  /**
   * Creates a new weekly schedule for the logged-in user.
   *
   * @param schedule The weekly schedule data to create.
   * @returns Observable of the created weekly schedule.
   */
  public create$(
    schedule: WeeklyScheduleDto,
  ): Observable<ResponseUserWeeklyScheduleDto> {
    const email: string = this._loggedUserService.currentUser().email;

    const body: CreateUserWeeklyScheduleDto = {
      tenant: environment.tenant,
      email,
      schedule,
    };

    const params: UserWeeklyScheduleControllerCreate$Params = {
      body,
    };

    return this._userWeeklyScheduleService.userWeeklyScheduleControllerCreate(
      params,
    );
  }

  /**
   * Updates the weekly schedule for the logged-in user.
   *
   * @param schedule The updated weekly schedule data.
   * @returns Observable of the updated weekly schedule.
   */
  public update$(
    schedule: WeeklyScheduleDto,
  ): Observable<ResponseUserWeeklyScheduleDto> {
    const email: string = this._loggedUserService.currentUser().email;

    const params: UserWeeklyScheduleControllerUpdate$Params = {
      tenant: environment.tenant,
      email,
      body: { schedule },
    };

    return this._userWeeklyScheduleService.userWeeklyScheduleControllerUpdate(
      params,
    );
  }

  /**
   * Deletes the weekly schedule for the logged-in user.
   *
   * @returns Observable of void when the deletion is successful.
   */
  public delete$(): Observable<void> {
    const email: string = this._loggedUserService.currentUser().email;

    const params: UserWeeklyScheduleControllerDelete$Params = {
      tenant: environment.tenant,
      email,
    };

    return this._userWeeklyScheduleService.userWeeklyScheduleControllerDelete(
      params,
    );
  }

  /**
   * Retrieves the weekly schedule for the logged-in user.
   *
   * @returns Observable of the user's weekly schedule.
   */
  public findOne$(): Observable<ResponseUserWeeklyScheduleDto> {
    const email: string = this._loggedUserService.currentUser().email;

    const params: UserWeeklyScheduleControllerFindOne$Params = {
      tenant: environment.tenant,
      email,
    };

    return this._userWeeklyScheduleService.userWeeklyScheduleControllerFindOne(
      params,
    );
  }
}
